'use client'

import { useCallback, useEffect, useState } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import qs from 'qs'
import { equals } from 'ramda'

import { useFilterChange } from 'modules/products'
import Checkbox from 'shared/ui/form/Checkbox'

import { FilterDictionary } from '../types'
import { createQueryString, FILTER_NAMES } from '../utils'

import { ClearButton } from './ClearButton'
import HorizontalExpendablePanel from './HorizontalExpendablePanel'
import { InputsGroup } from './styles'

interface TagsFilterProps {
  tags: FilterDictionary[]
  isActive?: boolean
}

const TagsFilter = ({ tags, isActive }: TagsFilterProps) => {
  const [value, setValue] = useState<string[] | null>(null)
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { setLastFilterChange } = useFilterChange()

  useEffect(() => {
    if (!searchParams) return
    const parsed = qs.parse(searchParams.toString())
    const queryValue = (parsed.filter as { tag?: string[] })?.tag || null

    if (!equals(value, queryValue)) {
      setValue(queryValue)
    }
  }, [searchParams, value])

  const updateQuery = (newValues: string[] | null) => {
    const newQueryString = createQueryString(
      new URLSearchParams(searchParams || ''),
      'tag',
      newValues
    )

    setLastFilterChange(new Date())
    router.replace(`${pathname}?${newQueryString}`)
  }

  const handleSelect = useCallback(
    (tagId: string) => () => {
      setValue(prev => {
        let currentValues = [...(prev || [])]

        if (currentValues.includes(tagId)) {
          currentValues = currentValues.filter(id => id !== tagId)
        } else {
          currentValues.push(tagId)
        }

        const valueToUse = currentValues.length > 0 ? currentValues : null

        updateQuery(valueToUse)

        return valueToUse
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setValue, searchParams, pathname, router]
  )

  const handleClear = useCallback(() => {
    setValue(null)
    updateQuery(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, pathname, router])

  const renderContent = () => (
    <InputsGroup>
      {tags?.map(tag => (
        <Checkbox
          key={tag.id}
          noMargin
          name={tag.id}
          onChange={handleSelect(tag.id)}
          value={value?.includes(tag.id) || false}
          label={tag.name}
        />
      ))}
    </InputsGroup>
  )

  return (
    <HorizontalExpendablePanel isActive={isActive} title={FILTER_NAMES.Type}>
      {isActive && (
        <ClearButton onClick={handleClear}>Wyczyść filtr</ClearButton>
      )}
      {renderContent()}
    </HorizontalExpendablePanel>
  )
}

export default TagsFilter
